import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  CreditCard,
  Settings,
  DollarSign,
  Percent,
  Save,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Crown,
  Users,
  Zap,
  Info,
  Calculator,
  TrendingUp,
  Shield
} from "lucide-react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

/**
 * StripeFeeSettings - Super Admin component to configure platform fees
 */
const StripeFeeSettings = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    free_fee: 10,
    pro_fee: 5,
    elite_fee: 5,
    pass_stripe_fees: true,
    stripe_percent: 2.9,
    stripe_fixed: 0.30
  });
  const [originalSettings, setOriginalSettings] = useState(null);
  const [previewAmount, setPreviewAmount] = useState(100);

  useEffect(() => {
    fetchSettings();
  }, [token]);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/stripe-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(res.data.settings);
      setOriginalSettings(res.data.settings);
    } catch (err) {
      // If endpoint doesn't exist yet, use defaults
      console.log("Using default settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/stripe-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOriginalSettings(settings);
      toast.success("Stripe fee settings saved successfully!");
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(settings) !== JSON.stringify(originalSettings);

  const calculatePreview = (tier) => {
    const feePercent = settings[`${tier}_fee`];
    const platformFee = (previewAmount * feePercent) / 100;
    const stripeFee = (previewAmount * settings.stripe_percent / 100) + settings.stripe_fixed;
    const totalFees = settings.pass_stripe_fees ? platformFee + stripeFee : platformFee;
    const stylistNet = previewAmount - totalFees;
    return { platformFee, stripeFee, totalFees, stylistNet };
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-8 text-center">
          <RefreshCw className="w-8 h-8 text-amber-400 animate-spin mx-auto mb-4" />
          <p className="text-zinc-400">Loading Stripe settings...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            Stripe Fee Configuration
          </h2>
          <p className="text-zinc-400 mt-1">
            Configure platform fees collected from stylist transactions
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className={`${hasChanges ? 'bg-green-500 hover:bg-green-600' : 'bg-zinc-700'} text-white`}
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <div className="flex items-center gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-amber-400" />
          <p className="text-amber-400">You have unsaved changes</p>
        </div>
      )}

      {/* Fee Tiers Configuration */}
      <div className="grid md:grid-cols-3 gap-6">
        {/* Free Tier */}
        <Card className="bg-zinc-900 border-zinc-700">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-zinc-700 rounded-lg flex items-center justify-center">
                  <Users className="w-4 h-4 text-zinc-400" />
                </div>
                <CardTitle className="text-white">Free Tier</CardTitle>
              </div>
              <Badge className="bg-zinc-700 text-zinc-300">Default</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-zinc-300 flex items-center justify-between">
                <span>Platform Fee</span>
                <span className="text-2xl font-bold text-white">{settings.free_fee}%</span>
              </Label>
              <Slider
                value={[settings.free_fee]}
                onValueChange={([val]) => setSettings(prev => ({ ...prev, free_fee: val }))}
                min={0}
                max={25}
                step={0.5}
                className="mt-3"
              />
              <div className="flex justify-between text-xs text-zinc-500 mt-1">
                <span>0%</span>
                <span>25%</span>
              </div>
            </div>
            
            {/* Preview */}
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <p className="text-xs text-zinc-500 mb-2">On ${previewAmount} payment:</p>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">You collect:</span>
                <span className="text-green-400 font-bold">${calculatePreview('free').platformFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Stylist gets:</span>
                <span className="text-white">${calculatePreview('free').stylistNet.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pro Tier */}
        <Card className="bg-zinc-900 border-amber-500/30">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <Zap className="w-4 h-4 text-amber-400" />
                </div>
                <CardTitle className="text-white">Pro Tier</CardTitle>
              </div>
              <Badge className="bg-amber-500/20 text-amber-400">Popular</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-zinc-300 flex items-center justify-between">
                <span>Platform Fee</span>
                <span className="text-2xl font-bold text-amber-400">{settings.pro_fee}%</span>
              </Label>
              <Slider
                value={[settings.pro_fee]}
                onValueChange={([val]) => setSettings(prev => ({ ...prev, pro_fee: val }))}
                min={0}
                max={25}
                step={0.5}
                className="mt-3"
              />
              <div className="flex justify-between text-xs text-zinc-500 mt-1">
                <span>0%</span>
                <span>25%</span>
              </div>
            </div>
            
            {/* Preview */}
            <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
              <p className="text-xs text-amber-400/70 mb-2">On ${previewAmount} payment:</p>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">You collect:</span>
                <span className="text-green-400 font-bold">${calculatePreview('pro').platformFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Stylist gets:</span>
                <span className="text-white">${calculatePreview('pro').stylistNet.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Elite Tier */}
        <Card className="bg-zinc-900 border-purple-500/30">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Crown className="w-4 h-4 text-purple-400" />
                </div>
                <CardTitle className="text-white">Elite Tier</CardTitle>
              </div>
              <Badge className="bg-purple-500/20 text-purple-400">Premium</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-zinc-300 flex items-center justify-between">
                <span>Platform Fee</span>
                <span className="text-2xl font-bold text-purple-400">{settings.elite_fee}%</span>
              </Label>
              <Slider
                value={[settings.elite_fee]}
                onValueChange={([val]) => setSettings(prev => ({ ...prev, elite_fee: val }))}
                min={0}
                max={25}
                step={0.5}
                className="mt-3"
              />
              <div className="flex justify-between text-xs text-zinc-500 mt-1">
                <span>0%</span>
                <span>25%</span>
              </div>
            </div>
            
            {/* Preview */}
            <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/20">
              <p className="text-xs text-purple-400/70 mb-2">On ${previewAmount} payment:</p>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">You collect:</span>
                <span className="text-green-400 font-bold">${calculatePreview('elite').platformFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Stylist gets:</span>
                <span className="text-white">${calculatePreview('elite').stylistNet.toFixed(2)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview Calculator */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Calculator className="w-5 h-5 text-amber-400" />
            Preview Calculator
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Adjust the payment amount to see fee breakdown across all tiers
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4 mb-6">
            <Label className="text-zinc-300 whitespace-nowrap">Payment Amount:</Label>
            <div className="relative flex-1 max-w-xs">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                type="number"
                value={previewAmount}
                onChange={(e) => setPreviewAmount(parseFloat(e.target.value) || 0)}
                className="bg-zinc-800 border-zinc-700 pl-8 text-xl font-bold"
                min="0"
                step="10"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left py-3 text-zinc-400 font-medium">Tier</th>
                  <th className="text-right py-3 text-zinc-400 font-medium">Platform Fee</th>
                  <th className="text-right py-3 text-zinc-400 font-medium">Stripe Fee</th>
                  <th className="text-right py-3 text-zinc-400 font-medium">Total Fees</th>
                  <th className="text-right py-3 text-zinc-400 font-medium">Stylist Net</th>
                  <th className="text-right py-3 text-green-400 font-medium">Your Revenue</th>
                </tr>
              </thead>
              <tbody>
                {['free', 'pro', 'elite'].map(tier => {
                  const preview = calculatePreview(tier);
                  return (
                    <tr key={tier} className="border-b border-zinc-800/50">
                      <td className="py-3">
                        <Badge className={
                          tier === 'free' ? 'bg-zinc-700 text-zinc-300' :
                          tier === 'pro' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-purple-500/20 text-purple-400'
                        }>
                          {tier.toUpperCase()}
                        </Badge>
                      </td>
                      <td className="text-right py-3 text-red-400">
                        -${preview.platformFee.toFixed(2)} ({settings[`${tier}_fee`]}%)
                      </td>
                      <td className="text-right py-3 text-red-400">
                        -${preview.stripeFee.toFixed(2)}
                      </td>
                      <td className="text-right py-3 text-red-400 font-medium">
                        -${preview.totalFees.toFixed(2)}
                      </td>
                      <td className="text-right py-3 text-white font-medium">
                        ${preview.stylistNet.toFixed(2)}
                      </td>
                      <td className="text-right py-3 text-green-400 font-bold">
                        ${preview.platformFee.toFixed(2)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Additional Settings */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Settings className="w-5 h-5 text-zinc-400" />
            Additional Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Pass Stripe Fees */}
          <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-white font-medium">Pass Stripe Fees to Stylist</p>
                <p className="text-sm text-zinc-400">
                  Deduct Stripe processing fees (2.9% + $0.30) from stylist payouts
                </p>
              </div>
            </div>
            <Switch
              checked={settings.pass_stripe_fees}
              onCheckedChange={(checked) => setSettings(prev => ({ ...prev, pass_stripe_fees: checked }))}
            />
          </div>

          {/* Stripe Fee Info */}
          <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-300">
                <p className="font-medium mb-2">Stripe Processing Fees (Fixed by Stripe)</p>
                <ul className="space-y-1 text-blue-300/80">
                  <li>• <strong>{settings.stripe_percent}%</strong> + <strong>${settings.stripe_fixed.toFixed(2)}</strong> per transaction</li>
                  <li>• These fees are charged by Stripe, not MyHairChair</li>
                  <li>• When enabled above, these are deducted from stylist payouts</li>
                  <li>• When disabled, MyHairChair absorbs these costs</li>
                </ul>
              </div>
            </div>
          </div>

          {/* No Fees for Non-Stripe */}
          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-green-300">
                <p className="font-medium mb-2">No Fees on Alternative Payments</p>
                <p className="text-green-300/80">
                  Zelle, CashApp, Venmo, and Cash payments have <strong>NO platform fees</strong>. 
                  Stylists keep 100% of these payments.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Live Status */}
      <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <Shield className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h3 className="text-white font-bold flex items-center gap-2">
                  Stripe Live Mode
                  <Badge className="bg-green-500 text-white">ACTIVE</Badge>
                </h3>
                <p className="text-green-400/80 text-sm">
                  Your Stripe account is connected and processing real payments
                </p>
              </div>
            </div>
            <Button variant="outline" className="border-green-500/50 text-green-400 hover:bg-green-500/10">
              View Stripe Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default StripeFeeSettings;
